/**
 * Serialize a chat conversation to Markdown and trigger a browser download.
 * (Spec → "Chat Agent Interface": export conversation as text/markdown — HALF1.)
 */

/**
 * @param {unknown} messages
 * @param {{ title?: string }} [opts]
 */
export function messagesToMarkdown(messages, { title } = {}) {
  const stamp = new Date().toISOString();
  const header = `# Chat conversation${title ? ` — ${title}` : ''}\n\n_Exported ${stamp}_\n`;
  const body = (Array.isArray(messages) ? messages : [])
    .map((m) => {
      const who = m.role === 'user' ? 'You' : 'AI Assistant';
      const content = m.content == null ? '' : String(m.content);
      return `## ${who}\n\n${content}\n`;
    })
    .join('\n');
  return `${header}\n${body}`;
}

export function downloadTextFile(filename, text, mime = 'text/markdown') {
  // Guard for non-browser/test environments without Blob/URL support.
  if (typeof document === 'undefined' || typeof Blob === 'undefined') return false;
  const blob = new Blob([text], { type: mime });
  const url = (typeof URL !== 'undefined' && URL.createObjectURL)
    ? URL.createObjectURL(blob) : null;
  const a = document.createElement('a');
  if (url) a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (url && URL.revokeObjectURL) URL.revokeObjectURL(url);
  return true;
}

// #397: download a BINARY file supplied base64-encoded by the API (e.g. .docx).
// Decodes base64 → bytes → Blob, then reuses the same anchor-click download path.
export function downloadBase64File(filename, base64, mime = 'application/octet-stream') {
  if (typeof document === 'undefined' || typeof Blob === 'undefined'
      || typeof atob === 'undefined') return false;
  const binary = atob(base64 || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  const url = (typeof URL !== 'undefined' && URL.createObjectURL)
    ? URL.createObjectURL(blob) : null;
  const a = document.createElement('a');
  if (url) a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (url && URL.revokeObjectURL) URL.revokeObjectURL(url);
  return true;
}

// #381 follow-up: download a BINARY Blob the API returned directly (responseType
// 'blob' — real .docx/.xlsx bytes, no base64 round-trip). Reuses the same
// anchor-click download path as the text/base64 helpers.
export function downloadBlob(filename, blob) {
  if (typeof document === 'undefined' || typeof Blob === 'undefined') return false;
  const data = blob instanceof Blob ? blob : new Blob([blob || '']);
  const url = (typeof URL !== 'undefined' && URL.createObjectURL)
    ? URL.createObjectURL(data) : null;
  const a = document.createElement('a');
  if (url) a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (url && URL.revokeObjectURL) URL.revokeObjectURL(url);
  return true;
}

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export { DOCX_MIME, XLSX_MIME };

/**
 * @param {unknown} messages
 * @param {{ title?: string }} [opts]
 */
export function exportConversation(messages, { title } = {}) {
  const md = messagesToMarkdown(messages, { title });
  const date = new Date().toISOString().slice(0, 10);
  return downloadTextFile(`chat-${date}.md`, md);
}
